import { assert } from './errors';
import { DT } from './util/dateTime';
import { YearMonth, YearMonthRecord, YM } from './yearMonth';

function isNumber(value: unknown): value is number {
    return typeof value === 'number';
}

export type LoanConfig = {
    amount: number; // Total loan amount
    payment: number; // Payment per period
    numPeriods: number; // Number of periods
    interestRate: number; // Interest rate per period, as decimal. EG 6% = 0.06
};

export type LoanConfigInput = Optional<LoanConfig>;

// Math source: https://www.calculatorsoup.com/calculators/financial/loan-calculator.php
export function makeLoanConfig(input: LoanConfigInput): LoanConfig {
    let pv = input.amount;
    let pmt = input.payment;
    const ir = input.interestRate;
    let n = input.numPeriods;

    if (isNumber(pv) && isNumber(pmt) && isNumber(ir) && isNumber(n)) {
        // If everything's already passed in, just return
        return { amount: pv, payment: pmt, interestRate: ir, numPeriods: n };
    } else if (!isNumber(pv) && isNumber(pmt) && isNumber(ir) && isNumber(n)) {
        pv = Math.round((pmt / ir) * (1 - 1 / Math.pow(1 + ir, n)));
    } else if (!isNumber(pmt) && isNumber(pv) && isNumber(ir) && isNumber(n)) {
        pmt = Math.round(
            (pv * ir * Math.pow(1 + ir, n)) / (Math.pow(1 + ir, n) - 1)
        );
    } else if (!isNumber(n) && isNumber(pv) && isNumber(pmt) && isNumber(ir)) {
        n = Math.ceil(Math.log(pmt / ir / (pmt / ir - pv)) / Math.log(1 + ir));
    } else {
        assert(false, 'Invalid params passed to makeLoanConfig');
    }

    return { amount: pv, payment: pmt, interestRate: ir, numPeriods: n };
}

type LoanSeriesValue = {
    balance: number;
    principlePayment: number;
    interestPayment: number;
};

export default class LoanSeries {
    start: YearMonth;
    end: YearMonth | null;
    config: LoanConfig;

    _values: YearMonthRecord<LoanSeriesValue> = {};
    _latestGenerated: YearMonth;

    constructor(start: YearMonth | DT | string, input: LoanConfigInput) {
        this.start = YM.make(start);
        this.config = makeLoanConfig(input);
        this.end = null;
        if (Number.isFinite(this.config.numPeriods)) {
            this.end = YM.add(this.start, this.config.numPeriods);
        }

        // Initialize series
        this._values[this.start] = {
            balance: this.config.amount,
            interestPayment: 0,
            principlePayment: 0,
        };
        this._latestGenerated = this.start;
    }

    balanceOn(ym: YearMonth): number {
        return this._valueOn(ym).balance;
    }

    paymentOn(ym: YearMonth): number {
        const { interestPayment, principlePayment } = this._valueOn(ym);
        return interestPayment + principlePayment;
    }

    _valueOn(ym: YearMonth): LoanSeriesValue {
        if (this.end && this.end < ym) {
            return { balance: 0, principlePayment: 0, interestPayment: 0 };
        }

        this._generateTo(ym);

        if (this._values[ym]) return this._values[ym];

        // This should be impossible. Throw an error.
        throw new Error('No end and no value for ' + ym);
    }

    _generateTo(ym: YearMonth) {
        if (this.end && this.end < ym) return;
        if (this._latestGenerated >= ym) return;

        const ir = this.config.interestRate;
        const payment = this.config.payment;

        let cursor = this._latestGenerated;
        let balance = this._values[cursor].balance;

        while (cursor < ym) {
            cursor = YM.add(cursor, 1);
            this._latestGenerated = cursor;
            const interestPayment = Math.round(balance * ir);

            // If we've reached the end, exit
            if (cursor === this.end) {
                this._values[cursor] = {
                    balance: 0,
                    principlePayment: balance,
                    interestPayment,
                };
                return;
            }

            // Otherwise, calculate principle payment and decrement balance
            // before continuing
            const principlePayment = payment - interestPayment;
            balance = balance - principlePayment;

            // This is loan series, balance can't go negative
            if (balance < 0) balance = 0;

            this._values[cursor] = {
                balance,
                principlePayment,
                interestPayment,
            };
        }
    }
}
